import "./EditPost.css";
import "../Feed/style.css";
import logger from "../utils/logger";

import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import TopNavBar from "../TopNavBar/TopNavBar.js";
import PostMedia from "../Post/PostMedia";

import { api, errorMessage } from "../../api/client";
import {
  attachPhotoToPost,
  deletePhoto,
  fetchPresignedUrls,
  uploadFileToSpaces,
} from "../../api/uploads";
import { useAuth } from "../../auth/AuthContext";
import StatusMessage, { errorStatus } from "../StatusMessage/StatusMessage";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function EditTitle({ title, setTitle, disabled }) {
  return (
    <div className="form-group">
      <label htmlFor="edit-title">Title:</label>
      <input
        type="text"
        id="edit-title"
        className="form-control"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength="500"
        required
        disabled={disabled}
      />
    </div>
  );
}

function EditContent({ content, setContent, disabled }) {
  return (
    <div className="form-group">
      <label htmlFor="edit-content">Content:</label>
      <textarea
        id="edit-content"
        className="form-control"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function ExistingImage({ image, onRemove }) {
  return (
    <div className="existing-image">
      <PostMedia photo={image} className="existing-image-thumb" />
      <button
        type="button"
        className="remove-image-button"
        onClick={onRemove}
        title="Remove image"
      >
        &times;
      </button>
    </div>
  );
}

// Each entry in `newImages` is either:
//   - a raw File object  (while presigning is in-flight), or
//   - { file, presignedUrl, contentType, spacesKey }  (resolved)
function NewImageList({ newImages, onRemoveNew }) {
  if (newImages.length === 0) return null;

  return (
    <div className="new-images-list">
      {newImages.map((upload, index) => {
        const file = upload.file ?? upload;
        const ready = !!upload.presignedUrl;

        return (
          <div
            key={index}
            className="new-image-item"
            style={{
              backgroundColor: ready ? "#f0fdf4" : "#fefce8",
              border: `1px solid ${ready ? "#bbf7d0" : "#fde68a"}`,
              borderRadius: "4px",
              padding: "8px",
              transition: "background-color 0.3s ease",
            }}
          >
            <span>
              {file.name} ({Math.round(file.size / 1024)} KB)
              {!ready && (
                <span style={{ marginLeft: "8px", color: "#92400e" }}>
                  ⏳ getting upload URL…
                </span>
              )}
            </span>
            <button
              type="button"
              className="remove-image-button"
              onClick={() => onRemoveNew(index)}
              title={`Remove ${file.name}`}
            >
              &times;
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ImageList({
  existingImages,
  onRemoveExisting,
  newImages,
  onAddImages,
  onRemoveNew,
  disabled,
}) {
  return (
    <div className="form-group">
      <label>Photos and videos:</label>
      {existingImages.length > 0 && (
        <div className="existing-images-grid">
          {existingImages.map((image, index) => (
            <ExistingImage
              key={image.photo_guid}
              image={image}
              onRemove={() => onRemoveExisting(index)}
            />
          ))}
        </div>
      )}
      <NewImageList newImages={newImages} onRemoveNew={onRemoveNew} />
      <input
        type="file"
        multiple
        accept="image/*,video/*"
        disabled={disabled}
        onChange={(e) => {
          onAddImages(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function EditPost() {
  const { session } = useAuth();
  const token = session.token;
  const { postGuid } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    async function loadPost() {
      try {
        const post = await api.get(`/family/posts/${postGuid}/`, token);
        setTitle(post.title);
        setContent(post.content);
        setExistingImages(post.photo_set || []);
      } catch (err) {
        logger.log(err);
        setError(errorMessage(err, "Couldn't load the post."));
      } finally {
        setIsLoading(false);
      }
    }
    loadPost();
  }, [postGuid, token]);

  const handleRemoveExisting = (index) => {
    const removed = existingImages[index];
    setImagesToDelete((prev) => [...prev, removed]);
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Called every time the user picks a new batch of files.
  // Files are shown immediately; presigned URLs are fetched in the background.
  const handleAddNewImages = async (files) => {
    if (files.length === 0) return;

    const insertionIndex = newImages.length;

    // Show files immediately as unresolved placeholders.
    setNewImages((prev) => [...prev, ...files]);

    try {
      const resolved = await fetchPresignedUrls(token, files);

      // Patch exactly the placeholder entries we just inserted.
      setNewImages((prev) => {
        const next = [...prev];
        resolved.forEach((r, i) => {
          next[insertionIndex + i] = r;
        });
        return next;
      });
    } catch (err) {
      setMessage(
        errorStatus(
          `Couldn't prepare the selected files: ${errorMessage(err)}`,
        ),
      );

      // Remove the unresolved placeholders so the user can try again.
      setNewImages((prev) =>
        prev.filter(
          (_, i) => i < insertionIndex || i >= insertionIndex + files.length,
        ),
      );
    }
  };

  const handleRemoveNew = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // the submit button is disabled until both of these hold
    if (!title.trim() || newImages.some((u) => !u.presignedUrl)) return;

    setIsSaving(true);
    setMessage(null);
    try {
      // 1. Save post title/content.
      await api.patch(`/family/posts/${postGuid}/`, token, { title, content });

      // 2. Upload all new files to Spaces in parallel.
      if (newImages.length > 0) {
        await Promise.all(newImages.map((u) => uploadFileToSpaces(u)));

        // 3. Attach each uploaded image to the post.
        await Promise.all(
          newImages.map((u) =>
            attachPhotoToPost(token, postGuid, u.spacesKey),
          ),
        );
      }

      // 4. Delete removed existing images.
      await Promise.allSettled(
        imagesToDelete.map((image) =>
          deletePhoto(token, postGuid, image.photo_guid),
        ),
      );

      navigate("/");
    } catch (err) {
      logger.log(err);
      setMessage(errorStatus(`Couldn't save changes: ${errorMessage(err)}`));
    } finally {
      setIsSaving(false);
    }
  };

  const allResolved = newImages.every((u) => !!u.presignedUrl);
  const canSubmit = !isSaving && title.trim() && allResolved;

  if (isLoading)
    return (
      <>
        <TopNavBar />
        <div className="edit-post-container">Loading...</div>
      </>
    );
  if (error)
    return (
      <>
        <TopNavBar />
        <div className="edit-post-container">Error: {error}</div>
      </>
    );

  return (
    <>
      <TopNavBar />
      <div className="edit-post-container">
        <h2>Edit Post</h2>
        <form className="edit-post-form" onSubmit={handleSubmit}>
          <EditTitle title={title} setTitle={setTitle} disabled={isSaving} />
          <ImageList
            existingImages={existingImages}
            onRemoveExisting={handleRemoveExisting}
            newImages={newImages}
            onAddImages={handleAddNewImages}
            onRemoveNew={handleRemoveNew}
            disabled={isSaving}
          />
          <EditContent
            content={content}
            setContent={setContent}
            disabled={isSaving}
          />
          <StatusMessage message={message} onDismiss={() => setMessage(null)} />
          <div className="button-group">
            <button
              type="submit"
              className="submit-button"
              disabled={!canSubmit}
            >
              {isSaving
                ? "Saving..."
                : !allResolved
                  ? "Preparing uploads…"
                  : "Save Changes"}
            </button>
            <Link to="/" className="cancel-button">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </>
  );
}
